import {
  Baby,
  BadgeCheck,
  CircleDollarSign,
  ShieldAlert,
  Store,
  ToyBrick,
  TriangleAlert,
  Bookmark,
  Eye,
  Film,
  Globe,
  Languages,
  LayoutGrid,
  Link2,
  type LucideIcon,
  Pin,
  Sparkles,
  ThumbsUp,
  UserPlus,
  Users
} from 'lucide-react';
import {type ReactNode} from 'react';
import {Tooltip, TooltipContent, TooltipTrigger} from '@kroll-resolver-crisp/ui-components-react';
import {TranslateToggle} from '@/components/shared/TranslateToggle';
import {type ItemStat, type ItemStatKind} from '@/lib/itemCard';
import {cn} from '@/lib/utils';

const STAT_ICONS: Record<ItemStatKind, LucideIcon> = {
  ageRestricted: ShieldAlert,
  contentWarning: TriangleAlert,
  safeForMinors: Baby,
  aiGenerated: Sparkles,
  views: Eye,
  likes: ThumbsUp,
  saves: Bookmark,
  audience: Users,
  following: UserPlus,
  videos: Film,
  pins: Pin,
  boards: LayoutGrid,
  country: Globe,
  domain: Link2,
  verified: BadgeCheck,
  verifiedMerchant: Store,
  monetized: CircleDollarSign,
  madeForKids: ToyBrick
};

/**
 The card's stats line: one icon-led value per stat the item's platform carries. The icon carries
 the label for assistive tech (so a stat reads "Views 184K") and the tooltip spells it out for a
 sighted analyst, since the icon is the only visible hint of what a bare number counts.
 */
export function ItemStats({
  stats,
  trailing,
  className
}: {
  readonly stats: readonly ItemStat[];
  // Rendered at the end of the line, e.g. the Translate link beside the language.
  readonly trailing?: ReactNode;
  readonly className?: string;
}) {
  if (stats.length === 0 && !trailing) {
    return null;
  }

  return (
    <div
      className={cn(
        'text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] font-medium tabular-nums',
        className
      )}>
      {stats.map((stat) => {
        const Icon = STAT_ICONS[stat.kind];
        return (
          <Tooltip key={stat.kind}>
            <TooltipTrigger
              render={
                <span
                  className={cn(
                    'inline-flex min-w-0 items-center gap-1',
                    stat.tone === 'destructive' && 'text-destructive-ink'
                  )}
                />
              }>
              {/* A flag already says what it is ("Verified merchant"), so its icon is decoration; a
                  bare number needs the icon to say what it counts. */}
              {stat.value === stat.label ? (
                <Icon className="size-3 shrink-0" aria-hidden="true" />
              ) : (
                <Icon className="size-3 shrink-0" aria-label={stat.label} />
              )}
              <span className="truncate">
                {stat.value}
                {stat.suffix ? ` ${stat.suffix}` : null}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {stat.detail ? `${stat.label}: ${stat.detail}` : stat.label}
            </TooltipContent>
          </Tooltip>
        );
      })}
      {trailing}
    </div>
  );
}

/**
 The item's language and the Translate link as one phrase, so the analyst knows what they'd be
 translating from: "French (declared), translate", and "French (declared), show original" once the
 translation is shown. It stays the declared language after translating rather than what Cloud
 Translation detected: detection runs per segment, and an item's short English tags and hashtags
 outvoted its title and description often enough that nearly every item read as English. An item
 declared English offers no link; one that declares nothing offers the link alone, since it may be
 in anything.
 */
export function ItemLanguage({
  itemKey,
  language,
  texts,
  offerTranslate
}: {
  readonly itemKey: string;
  readonly language: string | null;
  readonly texts: Array<string | null | undefined>;
  readonly offerTranslate: boolean;
}) {
  const canTranslate = offerTranslate && texts.some((text) => Boolean(text?.trim()));
  if (!language && !canTranslate) {
    return null;
  }

  return (
    <span className="inline-flex items-center gap-1">
      <Languages className="size-3 shrink-0" aria-hidden="true" />
      {language ? (
        <span title="The language the platform says this item is in">
          {language} <span className="font-normal">(declared)</span>
          {canTranslate ? ',' : null}
        </span>
      ) : null}
      {canTranslate ? <TranslateToggle inline itemKey={itemKey} texts={texts} /> : null}
    </span>
  );
}
